import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScaleUnbalanced } from '@fortawesome/free-solid-svg-icons';

// How much room a position gave you: how many of its legal moves were good. Shared by the move panel
// and the repair session. "Good" is the app's inaccuracy threshold, so the gauge agrees with the move's
// classification. The bar fills with the safe share.
export default function GoodMovesGauge(props: {
  goodMoves?: number | null;
  legalMoves?: number | null;
  // The count hit the engine's candidate ceiling, so all we know is "at least this many".
  saturated?: boolean | null;
}): React.ReactNode {
  const { goodMoves, legalMoves } = props;
  if (goodMoves == null || legalMoves == null || legalMoves <= 0) {
    return null;
  }
  const ceiling = props.saturated === true;
  const ratio = goodMoves / legalMoves;

  // Picked against real games: a normal middlegame leaves 35%+ of moves playable, a position where you
  // must find something sits in the teens and twenties, a tightrope in single figures. Ratios, so few
  // and many legal moves share a scale.
  const tone = ceiling ? 'wide' : ratio >= 0.35 ? 'wide' : ratio >= 0.15 ? 'narrow' : 'tightrope';

  const text = ceiling
    ? 'Plenty of good moves here.'
    : goodMoves === 0
      ? `No good moves here. All ${legalMoves} lose.`
      : `${ratio < 0.15 ? 'Only ' : ''}${goodMoves} of ${legalMoves} moves here ${
          goodMoves === 1 ? 'was' : 'were'
        } good.`;

  return (
    <div className={`cm-cp-goodmoves cm-cp-goodmoves-${tone}`}>
      <div className="cm-cp-goodmoves-head">
        <FontAwesomeIcon icon={faScaleUnbalanced} aria-hidden="true" />
        <span className="cm-cp-goodmoves-text">{text}</span>
      </div>
      <div
        className="cm-cp-goodmoves-meter"
        role="img"
        aria-label={
          ceiling
            ? 'Plenty of good moves in this position'
            : `${goodMoves} of ${legalMoves} legal moves were good`
        }
      >
        <span style={{ width: ceiling ? '100%' : `${Math.max(2, Math.round(ratio * 100))}%` }} />
      </div>
    </div>
  );
}
